import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, VIEW, EDIT, DATE_FORMAT } from "const"
import {
    MtStudy,
    MtStudyCreate,
} from "server/models"
import {
    useInput,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    StudyValidation,

    SaveStudy,
    SortRrsCategories,
    PrepareRrsCategoryNestedIds,

    closeModal,
    setStudyEdit,
    setStudyReloadList,
    setStudyReloadView,
    setStudyReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    DatePicker,
    UserDropdown,
    Empty,
    SelectDropdown,
    SelectDropdownItem,
    SelectDropdownSearch,
    Button,
    SpinnerIcon,
    TableNestedIds,
} from "client/components"

interface EditStudyProps {
    study: MtStudy
}

let isMounted: boolean

const Form = ({
    study,
}: EditStudyProps) => {
    const baseRoute = useSelector((state: StoreType) => state.study.baseRoute)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const categoryChildParent = useSelector((state: StoreType) => state.rrsCategory.categoryChildParent)

    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const { studyFields } = projectSettings.study.studyFieldsAssigned ? projectSettings.study : accountSettings.study

    const [name, setName] = useInput(study.id ? study.name : project.name)
    const [description, setDescription] = useInput(study.description)
    const [location, setLocation] = useInput(study.location)
    const [site, setSite] = useInput(study.site)
    const [plant, setPlant] = useInput(study.plant)
    const [group, setGroup] = useInput(study.group)
    const [unit, setUnit] = useInput(study.unit)
    const [subUnit, setSubUnit] = useInput(study.subUnit)

    const [dateStart, setDateStart] = useState(study.dateStart ? new Date(study.dateStart) : null)
    const [dateEnd, setDateEnd] = useState(study.dateEnd ? new Date(study.dateEnd) : null)
    const [managerId, setManagerId] = useState(study.managerId)
    const [systemStageId, setSystemStageId] = useState(study.systemStageId)
    const [statusId, setStatusId] = useState(study.statusId)

    const [systemStageNestedCategoryList, setSystemStageNestedCategoryList] = useState<any[]>([])
    const [systemStageCategorySearch, setSystemStageCategorySearch] = useState<string>("")
    const [systemStageCategory, setSystemStageCategory] = useState<string>(null)

    const [statusNestedCategoryList, setStatusNestedCategoryList] = useState<any[]>([])
    const [statusCategorySearch, setStatusCategorySearch] = useState<string>("")
    const [statusCategory, setStatusCategory] = useState<string>(null)

    const [validation, setValidation] = useState<StudyValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        if (!study.id) {
            // date start
            const start = new Date()
            setDateStart(start)

            // date end
            const end = new Date(start)
            end.setFullYear(end.getFullYear() + 1)
            setDateEnd(end)
        }
        return () => {
            isMounted = false
        }
    }, [])


    // "systemStage" category, nested list and search
    useEffect(() => {
        const categoriesFiltered = SortRrsCategories(Object.values(categoryList), { search: systemStageCategorySearch })
        const groupedIds = PrepareRrsCategoryNestedIds(categoriesFiltered)

        let parentArray: string[]
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(categoryChildParent[parentId])
            }
        }

        const nestedList: any[] = []
        const addNested = (nestedIds: readonly TableNestedIds[], level: number) => {
            nestedIds.forEach((row) => {
                const id = Number(row.id)
                const categoryItem = categoryList[id]
                let rowName: string

                if (systemStageCategorySearch.length) {
                    parentArray = []
                    addParent(categoryItem.id)
                    rowName = parentArray.join(" > ") // as minimum there will be searched category
                } else if (categoryItem.shortName.length) rowName = `${categoryItem.shortName} (${categoryItem.name})`
                else rowName = categoryItem.name

                nestedList.push(
                    <SelectDropdownItem
                        key={id}
                        onClick={() => setSystemStageId(id)}
                    >
                        <span style={{ paddingLeft: `${level * 30}px` }}>{rowName}</span>
                    </SelectDropdownItem>,
                )
                addNested(row.nestedIds, level + 1)
            })
        }
        if (groupedIds[0]) addNested(groupedIds[0].nestedIds, 0) // where groupedIds[0] - top level group

        setSystemStageNestedCategoryList(nestedList)
    }, [categoryList, categoryChildParent, systemStageCategorySearch])

    useEffect(() => {
        const parentArray: string[] = []
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(categoryChildParent[parentId])
            }
        }
        addParent(systemStageId)
        setSystemStageCategory(parentArray.length ? parentArray.join(" > ") : null)
    }, [systemStageId, categoryList, categoryChildParent])


    // "status" category, nested list and search
    useEffect(() => {
        const categoriesFiltered = SortRrsCategories(Object.values(categoryList), { search: statusCategorySearch })
        const groupedIds = PrepareRrsCategoryNestedIds(categoriesFiltered)

        let parentArray: string[]
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(categoryChildParent[parentId])
            }
        }

        const nestedList: any[] = []
        const addNested = (nestedIds: readonly TableNestedIds[], level: number) => {
            nestedIds.forEach((row) => {
                const id = Number(row.id)
                const categoryItem = categoryList[id]
                let rowName: string

                if (statusCategorySearch.length) {
                    parentArray = []
                    addParent(categoryItem.id)
                    rowName = parentArray.join(" > ") // as minimum there will be searched category
                } else if (categoryItem.shortName.length) rowName = `${categoryItem.shortName} (${categoryItem.name})`
                else rowName = categoryItem.name

                nestedList.push(
                    <SelectDropdownItem
                        key={id}
                        onClick={() => setStatusId(id)}
                    >
                        <span style={{ paddingLeft: `${level * 30}px` }}>{rowName}</span>
                    </SelectDropdownItem>,
                )
                addNested(row.nestedIds, level + 1)
            })
        }
        if (groupedIds[0]) addNested(groupedIds[0].nestedIds, 0) // where groupedIds[0] - top level group

        setStatusNestedCategoryList(nestedList)
    }, [categoryList, categoryChildParent, statusCategorySearch])

    useEffect(() => {
        const parentArray: string[] = []
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(categoryChildParent[parentId])
            }
        }
        addParent(statusId)
        setStatusCategory(parentArray.length ? parentArray.join(" > ") : null)
    }, [statusId, categoryList, categoryChildParent])


    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = study.id
            ? `${baseRoute}/${VIEW}/${study.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (dateEnd < dateStart) {
            setValidation({ dateStart: lang.dateEndLessDateStart })
            return
        }
        if (
            dateEnd.getDate() === dateStart.getDate()
            && dateEnd.getMonth() === dateStart.getMonth()
            && dateEnd.getFullYear() === dateStart.getFullYear()
        ) {
            setValidation({ dateStart: lang.dateEndEqualDateStart })
            return
        }

        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newStudyData: MtStudyCreate = {
            projectId: project.id,
            name,
            description,
            dateStart,
            dateEnd,
            managerId,
            systemStageId,
            statusId,
            location,
            site,
            plant,
            group,
            unit,
            subUnit,
        }

        SaveStudy(study.id, newStudyData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newStudyId) replaceHistoryState(`${baseRoute}/${EDIT}/${res.newStudyId}`)
            }
            if (res.validation) return

            dispatch(setStudyEdit({
                id: res.newStudyId || study.id,
                ...newStudyData,
            }))

            dispatch(setStudyReloadList(false))
            dispatch(setStudyReloadView(false))
            setTimeout(() => {
                dispatch(setStudyReloadList(true))
                dispatch(setStudyReloadView(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setStudyReloadEdit(false))
                setTimeout(() => {
                    dispatch(setStudyReloadEdit(true))
                }, 0)
            }
        })
    }

    const fields: any[] = []
    if (studyFields.systemStage) {
        fields.push(
            <FormGroup key="systemStage">
                <Label>{lang.systemStage}</Label>
                <SelectDropdown title={systemStageCategory || <Empty />}>
                    <SelectDropdownSearch
                        value={systemStageCategorySearch}
                        onChange={(e) => setSystemStageCategorySearch(e.target.value)}
                        onClearSearch={() => setSystemStageCategorySearch("")}
                    />
                    <SelectDropdownItem onClick={() => setSystemStageId(null)}>
                        <Empty />
                    </SelectDropdownItem>
                    { systemStageNestedCategoryList }
                </SelectDropdown>
                <Feedback>{validation && validation.systemStageId}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.studyStatus) {
        fields.push(
            <FormGroup key="studyStatus">
                <Label>{lang.status}</Label>
                <SelectDropdown title={statusCategory || <Empty />}>
                    <SelectDropdownSearch
                        value={statusCategorySearch}
                        onChange={(e) => setStatusCategorySearch(e.target.value)}
                        onClearSearch={() => setStatusCategorySearch("")}
                    />
                    <SelectDropdownItem onClick={() => setStatusId(null)}>
                        <Empty />
                    </SelectDropdownItem>
                    { statusNestedCategoryList }
                </SelectDropdown>
                <Feedback>{validation && validation.statusId}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.location) {
        fields.push(
            <FormGroup key="location">
                <Label>{lang.location}</Label>
                <Input type="text" value={location} onChange={setLocation} />
                <Feedback>{validation && validation.location}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.site) {
        fields.push(
            <FormGroup key="site">
                <Label>{lang.site}</Label>
                <Input type="text" value={site} onChange={setSite} />
                <Feedback>{validation && validation.site}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.plant) {
        fields.push(
            <FormGroup key="plant">
                <Label>{lang.plant}</Label>
                <Input type="text" value={plant} onChange={setPlant} />
                <Feedback>{validation && validation.plant}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.group) {
        fields.push(
            <FormGroup key="group">
                <Label>{lang.group}</Label>
                <Input type="text" value={group} onChange={setGroup} />
                <Feedback>{validation && validation.group}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.unit) {
        fields.push(
            <FormGroup key="unit">
                <Label>{lang.unit}</Label>
                <Input type="text" value={unit} onChange={setUnit} />
                <Feedback>{validation && validation.unit}</Feedback>
            </FormGroup>,
        )
    }
    if (studyFields.subUnit) {
        fields.push(
            <FormGroup key="subUnit">
                <Label>{lang.subUnit}</Label>
                <Input type="text" value={subUnit} onChange={setSubUnit} />
                <Feedback>{validation && validation.subUnit}</Feedback>
            </FormGroup>,
        )
    }

    const maxColumnLength = fields.length / 2 + (fields.length % 2)

    const fields1 = fields.slice(0, maxColumnLength)
    const fields2 = fields.slice(maxColumnLength)

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    {
                        studyFields.description && (
                            <FormGroup>
                                <Label>{lang.description}</Label>
                                <Textarea value={description} onChange={setDescription} />
                                <Feedback>{validation && validation.description}</Feedback>
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.timeframe}</Label>
                        <FormGroup inline>
                            <DatePicker
                                selected={dateStart}
                                onChange={(date: Date) => {
                                    if (!date) setDateStart(dateStart)
                                    else setDateStart(date)
                                }}
                                dateFormat={DATE_FORMAT}
                            />
                            <DatePicker
                                selected={dateEnd}
                                onChange={(date: Date) => {
                                    if (!date) setDateEnd(dateEnd)
                                    else setDateEnd(date)
                                }}
                                dateFormat={DATE_FORMAT}
                            />
                        </FormGroup>
                        <Feedback>{validation && validation.dateStart}</Feedback>
                    </FormGroup>

                    {
                        studyFields.manager && (
                            <FormGroup>
                                <Label>{lang.manager}</Label>
                                <UserDropdown
                                    selectedId={managerId}
                                    listIds={(projectUserList && projectUserList[project.id]) || []}
                                    title={lang.changeManager}
                                    onChange={(userId) => setManagerId(userId)}
                                    showDelete
                                />
                            </FormGroup>
                        )
                    }
                </Col>

                {
                    (fields1.length && fields2.length)
                        ? ([
                            <Col key="fields1" xs={6}>
                                {fields1.map((field) => field)}
                            </Col>,
                            <Col key="fields2" xs={6}>
                                {fields2.map((field) => field)}
                            </Col>,
                        ])
                        : (
                            <Col xs={12}>
                                {fields.map((field) => field)}
                            </Col>
                        )
                }

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
